import { useCallback, useEffect, useRef, useState } from "react";
import type { StoreSuggestion } from "~/lib/storeSuggestion";

const SEEN_KEY = "wv-store-suggestion-seen";
const RESULT_KEY = "wv-store-suggestion";
const SEEN_DAYS = 30;
// wovar.nl, wovar.co.uk, be.wovar.io ... but not wovar.someblog.net.
const WOVAR_HOST = /(^|\.)wovar\.[a-z]{2,3}(\.[a-z]{2})?$/;

const read = (storage: () => Storage, key: string) => {
  try {
    return storage().getItem(key);
  } catch {
    return null;
  }
};

const write = (storage: () => Storage, key: string, value: string) => {
  try {
    storage().setItem(key, value);
  } catch {}
};

// The page's hreflang links hold its exact URL on every other store (no argument: any of them).
export const alternateHref = (hreflang = "") =>
  document.querySelector<HTMLLinkElement>(`link[rel="alternate"][hreflang${hreflang ? `="${hreflang}"` : ""}]`)?.href;

// Waits until the page is idle so the check never competes with page load.
const whenIdle = (fn: () => void) =>
  typeof window.requestIdleCallback === "function"
    ? window.requestIdleCallback(fn, { timeout: 3000 })
    : window.setTimeout(fn, 1500);

const local = () => window.localStorage;
const session = () => window.sessionStorage;

const readCached = (key: string): { suggestion: StoreSuggestion | null } | null => {
  try {
    return JSON.parse(read(session, key) ?? "null");
  } catch {
    return null;
  }
};

// Shown once: after the first time it was on screen (or acted on) it stays away for 30 days.
const wasSeen = () => Date.now() - Number(read(local, SEEN_KEY) ?? 0) < SEEN_DAYS * 24 * 60 * 60 * 1000;

const remember = () => write(local, SEEN_KEY, String(Date.now()));

// Arriving from another Wovar store means the visitor already chose this one on purpose.
const cameFromOtherStore = () => {
  try {
    const host = document.referrer ? new URL(document.referrer).hostname : "";
    return !!host && host !== window.location.hostname && WOVAR_HOST.test(host);
  } catch {
    return false;
  }
};

export const useStoreSuggestion = (locale: string, enabled: boolean, pageKey: string) => {
  const [suggestion, setSuggestion] = useState<StoreSuggestion | null>(null);
  const requested = useRef(false);

  useEffect(() => {
    // Pages without hreflang links (account, search) can't be linked to: wait for one that can.
    if (!enabled || requested.current || !alternateHref()) {
      return;
    }
    requested.current = true;

    if (wasSeen()) {
      return;
    }
    if (cameFromOtherStore()) {
      remember();
      return;
    }
    // Most visitors browse in the store's own language: skip the request entirely.
    const primary = (navigator.languages?.[0] ?? navigator.language ?? "").split("-")[0].toLowerCase();
    if (primary === locale.split("-")[0]) {
      return;
    }
    const cached = readCached(RESULT_KEY);
    if (cached) {
      setSuggestion(cached.suggestion);
      return;
    }

    whenIdle(() => {
      fetch("/resources/store-suggestion", { headers: { Accept: "application/json" } })
        .then((res) => (res.ok ? res.json() : { suggestion: null }))
        .then((data: { suggestion: StoreSuggestion | null }) => {
          write(session, RESULT_KEY, JSON.stringify({ suggestion: data.suggestion }));
          setSuggestion(data.suggestion);
        })
        .catch(() => {});
    });
  }, [enabled, locale, pageKey]);

  const hide = useCallback(() => setSuggestion(null), []);

  return { suggestion, hide, remember };
};
